import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Checkbox,
  Chip,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  DataGrid,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  type GridColDef,
  type GridColumnVisibilityModel,
  type GridRenderCellParams,
} from '@mui/x-data-grid';
import BugReportIcon from '@mui/icons-material/BugReport';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ArticleIcon from '@mui/icons-material/Article';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useQueryClient } from '@tanstack/react-query';
import { downloadBlob, deleteWp, setSubtasks } from '../../api/dev';
import { useSnackbar } from '../../context/SnackbarContext';
import { BugReportDialog } from './BugReportDialog';
import type { WorkPackage, WpStatus } from '../../types/dev';

const STATUS_COLORS: Record<WpStatus, string> = {
  done: '#43a047',
  in_progress: '#1976d2',
  upcoming: '#9e9e9e',
  blocked: '#e53935',
};

const STATUS_LABELS: Record<WpStatus, string> = {
  done: '✅ Done',
  in_progress: '🔵 In Progress',
  upcoming: '⚪ Upcoming',
  blocked: '🔴 Blocked',
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

type TableRow = WorkPackage & { _isDetail?: boolean };

// ── Subtask panel ──────────────────────────────────────────────────────────

function SubtaskPanel({ wp, onToggle }: { wp: WorkPackage; onToggle: (id: number, idx: number) => void }) {
  const done = wp.subtasks!.filter((t) => t.done).length;
  return (
    <Box sx={{ px: 3, py: 1.5, bgcolor: 'action.hover', width: '100%' }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.07em', fontWeight: 600 }}>
        Subtasks — {done} / {wp.subtasks!.length} completed
      </Typography>
      <Stack sx={{ mt: 1 }}>
        {wp.subtasks!.map((task, idx) => (
          <FormControlLabel
            key={idx}
            control={<Checkbox checked={task.done} onChange={() => onToggle(wp.id, idx)} size="small" />}
            label={
              <Typography variant="body2" sx={{ textDecoration: task.done ? 'line-through' : 'none', color: task.done ? 'text.disabled' : 'text.primary' }}>
                {task.text}
              </Typography>
            }
          />
        ))}
      </Stack>
    </Box>
  );
}

// ── Actions cell ───────────────────────────────────────────────────────────

interface ActionsCellProps {
  row: WorkPackage;
  expanded: boolean;
  onExpand: () => void;
  onBugOpen: (id: number) => void;
  onDeleted: () => void;
}

function ActionsCell({ row, expanded, onExpand, onBugOpen, onDeleted }: ActionsCellProps) {
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  const handleDelete = async () => {
    if (!confirm(`Delete ${row.wp_number} "${row.title}"? This cannot be undone.`)) return;
    try {
      await deleteWp(row.id);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
      onDeleted();
    } catch {
      showSnackbar('Failed to delete work package', 'error');
    }
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {row.subtasks?.length ? (
        <Tooltip title={expanded ? 'Collapse subtasks' : 'Expand subtasks'}>
          <IconButton size="small" onClick={(e) => { e.stopPropagation(); onExpand(); }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      ) : null}
      <Tooltip title="Report bug">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onBugOpen(row.id); }}>
          <BugReportIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      {row.status === 'upcoming' && (
        <Tooltip title="Delete">
          <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); void handleDelete(); }}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// ── Toolbar ────────────────────────────────────────────────────────────────

function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ justifyContent: 'flex-end', px: 1, pt: 0.5 }}>
      <GridToolbarColumnsButton />
    </GridToolbarContainer>
  );
}

// ── WpTable ────────────────────────────────────────────────────────────────

const DEFAULT_COL_VISIBILITY: GridColumnVisibilityModel = { solved_at: false, priority: false };
const LS_KEY = 'dev-col-visibility';

interface Props {
  rows: WorkPackage[];
  onRowClick: (wpNumber: string) => void;
}

export function WpTable({ rows, onRowClick }: Props) {
  const [bugTargetId, setBugTargetId] = useState<number | null>(null);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);
  const [columnVisibility, setColumnVisibility] = useState<GridColumnVisibilityModel>(DEFAULT_COL_VISIBILITY);
  const queryClient = useQueryClient();
  const showSnackbar = useSnackbar();

  useEffect(() => {
    const saved = localStorage.getItem(LS_KEY);
    if (saved) {
      try { setColumnVisibility(JSON.parse(saved) as GridColumnVisibilityModel); } catch { /* ignore */ }
    }
  }, []);

  const handleVisibilityChange = (model: GridColumnVisibilityModel) => {
    setColumnVisibility(model);
    localStorage.setItem(LS_KEY, JSON.stringify(model));
  };

  const handleSubtaskToggle = async (wpId: number, idx: number) => {
    const wp = rows.find((r) => r.id === wpId);
    if (!wp?.subtasks) return;
    const updated = wp.subtasks.map((t, i) => (i === idx ? { ...t, done: !t.done } : t));
    try {
      await setSubtasks(wpId, updated);
      void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
    } catch {
      showSnackbar('Failed to update subtask', 'error');
    }
  };

  const allNullSubtasks = rows.every((r) => r.subtasks == null);

  // Inject detail rows after expanded parent. Detail rows inherit all parent
  // fields so DataGrid sorting keeps them adjacent (same sort key = stable position).
  const tableRows = useMemo<TableRow[]>(() => {
    const result: TableRow[] = [];
    for (const row of rows) {
      result.push(row);
      if (expandedRowId === row.id && row.subtasks?.length) {
        result.push({ ...row, id: -row.id, _isDetail: true });
      }
    }
    return result;
  }, [rows, expandedRowId]);

  const handleDownload = async (e: React.MouseEvent, id: number) => {
    e.stopPropagation();
    try {
      const blob = await downloadBlob(id);
      const row = rows.find((r) => r.id === id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = row?.blob_filename ?? `wp-${id}.md`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      showSnackbar('Download failed', 'error');
    }
  };

  const bugTarget = bugTargetId != null ? rows.find((r) => r.id === bugTargetId) : null;

  const columns: GridColDef<TableRow>[] = [
    {
      field: 'wp_number',
      headerName: 'WP',
      width: 90,
      sortComparator: (a: string, b: string) => {
        const n = (s: string) => parseInt(s.replace(/\D/g, ''), 10) || 0;
        return n(a) - n(b);
      },
      // Detail rows span all columns; first column renders the SubtaskPanel
      colSpan: (params) => params.row?._isDetail ? 999 : undefined,
      renderCell: (p: GridRenderCellParams<TableRow, string>) => {
        if (p.row._isDetail) {
          const parent = rows.find((r) => r.id === -p.row.id);
          return parent?.subtasks?.length ? (
            <SubtaskPanel wp={parent} onToggle={handleSubtaskToggle} />
          ) : null;
        }
        return (
          <Chip label={p.value} size="small" variant="outlined" sx={{ fontFamily: 'monospace', fontWeight: 700, fontSize: '0.75rem' }} />
        );
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 160,
      renderCell: (p: GridRenderCellParams<TableRow, string>) => (
        <Typography variant="body2" fontWeight={600} noWrap>{p.value}</Typography>
      ),
    },
    {
      field: 'phase',
      headerName: 'Phase',
      width: 70,
      renderCell: (p: GridRenderCellParams<TableRow, number | null>) =>
        p.value != null ? (
          <Chip label={`P${p.value}`} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'priority',
      headerName: 'Priority',
      width: 80,
      renderCell: (p: GridRenderCellParams<TableRow, number>) => (
        <Typography variant="body2">{p.value}</Typography>
      ),
    },
    {
      field: 'is_idea',
      headerName: 'Type',
      width: 90,
      renderCell: (p: GridRenderCellParams<TableRow, boolean>) =>
        p.value ? (
          <Chip label="💡 Idea" size="small" sx={{ bgcolor: '#fbc02d', color: '#000', fontSize: '0.7rem' }} />
        ) : (
          <Chip label="📦 WP" size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
        ),
    },
    ...(!allNullSubtasks ? [
      {
        field: 'subtasks',
        headerName: 'Subtasks',
        width: 170,
        sortable: false,
        renderCell: (p: GridRenderCellParams<TableRow>) => {
          const subtasks = p.row.subtasks;
          if (!subtasks) return <Typography variant="body2" color="text.secondary">—</Typography>;
          const done = subtasks.filter((t) => t.done).length;
          const total = subtasks.length;
          const pct = total > 0 ? Math.round((done / total) * 100) : 0;
          return (
            <Box sx={{ width: '100%' }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="caption">{done} / {total} done</Typography>
                <Typography variant="caption" color="text.secondary">{pct}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={pct} color={pct === 100 ? 'success' : 'primary'} sx={{ mt: 0.5 }} />
            </Box>
          );
        },
      } as GridColDef<TableRow>,
      {
        field: 'next_task',
        headerName: 'Next Task',
        width: 220,
        sortable: false,
        valueGetter: (_value: unknown, row: TableRow) =>
          row.subtasks?.find((t) => !t.done)?.text ?? null,
        renderCell: (p: GridRenderCellParams<TableRow>) => {
          if (!p.row.subtasks) return <Typography variant="body2" color="text.secondary">—</Typography>;
          const allDone = p.row.subtasks.every((t) => t.done);
          const next = p.row.subtasks.find((t) => !t.done);
          if (allDone) return (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <CheckCircleIcon sx={{ color: 'success.main', fontSize: 14 }} />
              <Typography variant="caption" color="success.main">All done</Typography>
            </Stack>
          );
          return (
            <Tooltip title={next?.text ?? ''}>
              <Typography variant="caption" color="text.secondary" noWrap>{next?.text}</Typography>
            </Tooltip>
          );
        },
      } as GridColDef<TableRow>,
    ] : []),
    {
      field: 'status',
      headerName: 'Status',
      width: 140,
      renderCell: (p: GridRenderCellParams<TableRow, WpStatus>) => (
        <Chip label={STATUS_LABELS[p.value!]} size="small" sx={{ bgcolor: STATUS_COLORS[p.value!], color: '#fff', fontSize: '0.7rem' }} />
      ),
    },
    {
      field: 'bug_count',
      headerName: 'Bugs',
      width: 80,
      renderCell: (p: GridRenderCellParams<TableRow, number>) =>
        p.value! > 0 ? (
          <Chip icon={<BugReportIcon />} label={p.value} size="small" sx={{ bgcolor: '#c62828', color: '#fff' }} />
        ) : (
          <CheckCircleIcon sx={{ color: '#43a047', fontSize: 20 }} />
        ),
    },
    {
      field: 'has_blob',
      headerName: 'Blob',
      width: 60,
      sortable: false,
      renderCell: (p: GridRenderCellParams<TableRow, boolean>) =>
        p.value ? (
          <Tooltip title="Download markdown">
            <IconButton size="small" onClick={(e) => void handleDownload(e, p.row.id)}>
              <ArticleIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          <Typography variant="body2" color="text.secondary">—</Typography>
        ),
    },
    {
      field: 'generated_at',
      headerName: 'Generated',
      width: 110,
      renderCell: (p: GridRenderCellParams<TableRow, string | null>) => (
        <Typography variant="body2">{fmtDate(p.value)}</Typography>
      ),
    },
    {
      field: 'inserted_at',
      headerName: 'Inserted',
      width: 110,
      renderCell: (p: GridRenderCellParams<TableRow, string>) => (
        <Typography variant="body2">{fmtDate(p.value)}</Typography>
      ),
    },
    {
      field: 'solved_at',
      headerName: 'Solved',
      width: 110,
      renderCell: (p: GridRenderCellParams<TableRow, string | null>) => (
        <Typography variant="body2">{fmtDate(p.value)}</Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: (p: GridRenderCellParams<TableRow>) => (
        <ActionsCell
          row={p.row as WorkPackage}
          expanded={expandedRowId === p.row.id}
          onExpand={() => setExpandedRowId(expandedRowId === p.row.id ? null : p.row.id)}
          onBugOpen={(id) => setBugTargetId(id)}
          onDeleted={() => void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] })}
        />
      ),
    },
  ];

  return (
    <>
      <DataGrid
        rows={tableRows}
        columns={columns}
        density="compact"
        sx={{ cursor: 'pointer', '& .MuiDataGrid-row:hover': { bgcolor: 'action.hover' } }}
        disableRowSelectionOnClick={false}
        onRowClick={(params) => {
          if ((params.row as TableRow)._isDetail) return;
          onRowClick(params.row.wp_number as string);
        }}
        getRowHeight={(params) => params.model._isDetail ? 'auto' : undefined}
        getEstimatedRowHeight={() => 36}
        columnVisibilityModel={columnVisibility}
        onColumnVisibilityModelChange={handleVisibilityChange}
        slots={{ toolbar: CustomToolbar }}
        initialState={{
          pagination: { paginationModel: { pageSize: 25 } },
          sorting: { sortModel: [{ field: 'wp_number', sort: 'desc' }] },
        }}
        pageSizeOptions={[25, 50, 100]}
      />

      {bugTarget && (
        <BugReportDialog
          open={bugTargetId != null}
          wpId={bugTarget.id}
          wpNumber={bugTarget.wp_number}
          wpTitle={bugTarget.title}
          onClose={() => setBugTargetId(null)}
          onReported={() => {
            void queryClient.invalidateQueries({ queryKey: ['dev', 'wps'] });
            setBugTargetId(null);
          }}
        />
      )}
    </>
  );
}
